'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { Button } from '@/components/v2/ui/button'
import Icon from '@/components/v2/ui/icon'
import { useTexts } from '@/contexts/LanguageContext'
import { useTrustCenterConfig } from '@/contexts/TrustCenterContext'
import { cn } from '@/lib/utils'
import { TRUST_CENTER_SECTIONS, trustCenterPath } from '../utils'
import { useDocuments } from '../resources/useDocuments'
import PoweredBy from './PoweredBy'
import RequestAccessDialog from './RequestAccessDialog/RequestAccessDialog'

function HeaderNav({ orgToken }: { orgToken: string }) {
  const { NAV } = useTexts().PUBLIC_TRUST_CENTER
  const { showControls } = useTrustCenterConfig()
  const pathname = usePathname()
  const items = [
    {
      href: trustCenterPath(orgToken, TRUST_CENTER_SECTIONS.CERTIFICATIONS),
      label: NAV.CERTIFICATIONS,
      isVisible: true,
    },
    { href: trustCenterPath(orgToken, TRUST_CENTER_SECTIONS.CONTROLS), label: NAV.CONTROLS, isVisible: showControls },
    { href: trustCenterPath(orgToken, TRUST_CENTER_SECTIONS.RESOURCES), label: NAV.DOCUMENTS, isVisible: true },
    { href: trustCenterPath(orgToken, TRUST_CENTER_SECTIONS.SUBPROCESSORS), label: NAV.SUBPROCESSORS, isVisible: true },
  ]
  return (
    <nav className="hidden items-center gap-6 text-sm text-trust-center-muted md:flex">
      {items
        .filter((item) => item.isVisible)
        .map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              'hover:text-trust-center-accent',
              pathname.startsWith(item.href) && 'font-medium text-trust-center',
            )}
          >
            {item.label}
          </Link>
        ))}
    </nav>
  )
}

function Brand({ orgToken }: { orgToken: string }) {
  const { PUBLIC_TRUST_CENTER } = useTexts()
  const { organizationName, logoPath } = useTrustCenterConfig()
  return (
    <Link href={trustCenterPath(orgToken)} className="flex items-center gap-4">
      {logoPath ? (
        <>
          <Image
            src={logoPath}
            alt={organizationName ?? ''}
            width={120}
            height={44}
            className="h-11 w-auto object-contain"
          />
          <span className="h-6 w-px bg-trust-center-border" />
        </>
      ) : (
        organizationName && <span className="text-xl font-bold tracking-tight">{organizationName}</span>
      )}
      <span className="text-xs font-medium tracking-widest text-trust-center-muted uppercase">
        {PUBLIC_TRUST_CENTER.TITLE}
      </span>
    </Link>
  )
}

export default function Header() {
  const { PUBLIC_TRUST_CENTER } = useTexts()
  const { orgToken } = useParams<{ orgToken: string }>()
  const { privateNames } = useDocuments()
  return (
    <header className="sticky top-0 z-30 border-b border-trust-center-border bg-white">
      <div className="mx-auto flex h-20 w-full max-w-screen-2xl items-center gap-5 px-4 md:px-12">
        <Brand orgToken={orgToken} />
        <span className="flex-1" />
        <HeaderNav orgToken={orgToken} />
        <span className="hidden h-6 w-px bg-trust-center-border md:block" />
        <PoweredBy className="hidden lg:flex" logoClassName="bg-trust-center" />
        {privateNames.length > 0 && (
          <RequestAccessDialog options={privateNames}>
            <Button className="rounded bg-trust-center text-white hover:bg-trust-center-deep">
              <Icon icon="lock" className="size-4" />
              {PUBLIC_TRUST_CENTER.NAV.REQUEST_ACCESS}
            </Button>
          </RequestAccessDialog>
        )}
      </div>
    </header>
  )
}
